import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u.startsWith("/") ? `${API}${u}` : u);

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

export const GallerySlider = () => {
  const [photos, setPhotos] = useState([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/gallery`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setPhotos(data.slice(0, 10)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (photos.length < 2) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % photos.length), 4500);
    return () => clearInterval(timer);
  }, [photos.length]);

  if (!photos.length) return null;

  const go = (dir) => setIndex((i) => (i + dir + photos.length) % photos.length);

  return (
    <section data-testid="home-gallery-slider" className="py-16 md:py-24 bg-[#F6F3EC]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="caption-text text-accent mb-3">Life at the Resort</p>
            <h2 className="font-heading text-4xl md:text-6xl font-light text-foreground tracking-tight">
              Moments at Serenity Village
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="slider-view-gallery"
            className="font-body text-sm text-primary hover:text-accent transition-colors inline-flex items-center gap-1"
          >
            View Full Gallery <ArrowRight size={15} />
          </Link>
        </motion.div>

        <motion.div {...fadeUp} className="relative rounded-2xl overflow-hidden group" style={{ height: "min(62vh, 560px)" }}>
          {photos.map((p, i) => (
            <img
              key={p.id}
              src={resolveUrl(p.url)}
              alt={p.caption || "Texoma Resorts"}
              loading={i === 0 ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: i === index ? 1 : 0 }}
            />
          ))}
          <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/60 to-transparent" />
          {photos[index]?.caption && (
            <p data-testid="slider-caption" className="absolute bottom-5 left-6 font-body text-sm text-white/90">
              {photos[index].caption}
            </p>
          )}
          <button
            data-testid="slider-prev"
            onClick={() => go(-1)}
            aria-label="Previous photo"
            className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 hover:bg-white text-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="slider-next"
            onClick={() => go(1)}
            aria-label="Next photo"
            className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 hover:bg-white text-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-5 right-6 flex gap-1.5">
            {photos.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setIndex(i)}
                aria-label={`Photo ${i + 1}`}
                className="h-1.5 rounded-full transition-all"
                style={{ width: i === index ? 22 : 8, background: i === index ? "#C06E52" : "rgba(255,255,255,0.6)" }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
